// =============================================================================
// BP-07-03: ResourceSearchBar Component
// Search input with suggestions and recent searches
// =============================================================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, X, Clock, TrendingUp, Loader2, ArrowRight } from 'lucide-react';
import { useResourceSearch, useDiagnosisSearch } from '@/features/resources/hooks/useResourceSearch';
import { useResourceStore, useSearchHistoryStore } from '@/features/resources/stores/resourceStore';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface ResourceSearchBarProps {
  variant?: 'default' | 'hero' | 'compact';
  placeholder?: string;
  autoFocus?: boolean;
  showSuggestions?: boolean;
  onSearch?: (query: string) => void;
  className?: string;
}

interface SuggestionItem {
  type: 'resource' | 'diagnosis' | 'recent' | 'trending';
  id: string;
  title: string;
  slug?: string;
  meta?: string;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function ResourceSearchBar({
  variant = 'default',
  placeholder = 'Search resources, diagnoses, topics...',
  autoFocus = false,
  showSuggestions = true,
  onSearch,
  className,
}: ResourceSearchBarProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const { setSearchQuery } = useResourceStore();
  const { searchHistory: recentSearches, addToHistory: addSearch, clearHistory: clearSearches } = useSearchHistoryStore();
  
  // Search hooks with debouncing
  const { data: searchResults, isLoading: isSearching } = useResourceSearch(
    { q: query, limit: 5 },
    query.length >= 2
  );
  
  const { data: diagnosisResults } = useDiagnosisSearch(
    query.length >= 2 ? query : ''
  );

  // Build suggestions list
  const suggestions: SuggestionItem[] = [];
  
  if (query.length >= 2) {
    // Add diagnosis matches first
    diagnosisResults?.slice(0, 2).forEach((diagnosis) => {
      suggestions.push({
        type: 'diagnosis',
        id: diagnosis.id,
        title: diagnosis.name,
        slug: diagnosis.slug,
        meta: 'Diagnosis',
      });
    });
    
    // Add resource matches
    searchResults?.resources?.slice(0, 5).forEach((resource) => {
      suggestions.push({
        type: 'resource',
        id: resource.id,
        title: resource.title,
        slug: resource.slug,
        meta: resource.category?.name,
      });
    });
  } else if (isFocused) {
    // Show recent searches when focused with no query
    (recentSearches ?? []).slice(0, 5).forEach((search, index) => {
      suggestions.push({
        type: 'recent',
        id: `recent-${index}`,
        title: search,
      });
    });

    // Add trending suggestions
    const trending = ['Cerebral Palsy', 'Therapy Options', 'IEP Guide', 'Insurance'];
    trending.forEach((term, index) => {
      if (!(recentSearches ?? []).includes(term)) {
        suggestions.push({
          type: 'trending',
          id: `trending-${index}`,
          title: term,
        });
      }
    });
  }

  // Handle search submission
  const handleSearch = useCallback((searchQuery: string) => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) return;
    
    addSearch(trimmedQuery);
    setSearchQuery(trimmedQuery);
    
    if (onSearch) {
      onSearch(trimmedQuery);
    } else {
      navigate(`/resources/search?q=${encodeURIComponent(trimmedQuery)}`);
    }
    
    setIsFocused(false);
    inputRef.current?.blur();
  }, [addSearch, setSearchQuery, onSearch, navigate]);

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: SuggestionItem) => {
    if (suggestion.type === 'diagnosis' && suggestion.slug) {
      navigate(`/diagnoses/${suggestion.slug}`);
    } else if (suggestion.type === 'resource' && suggestion.slug) {
      navigate(`/resources/${suggestion.slug}`);
    } else {
      handleSearch(suggestion.title);
    }
    setIsFocused(false);
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearch(query);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else {
          handleSearch(query);
        }
        break;
      case 'Escape':
        setIsFocused(false);
        inputRef.current?.blur();
        break;
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !inputRef.current?.contains(e.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset selected index when suggestions change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [suggestions.length, query]);

  // Variant styles
  const variantStyles = {
    default: {
      container: 'h-12',
      input: 'text-base',
      icon: 'w-5 h-5',
    },
    hero: {
      container: 'h-14 md:h-16',
      input: 'text-lg',
      icon: 'w-6 h-6',
    },
    compact: {
      container: 'h-10',
      input: 'text-sm',
      icon: 'w-4 h-4',
    },
  };

  const styles = variantStyles[variant];
  const showDropdown = isFocused && showSuggestions && suggestions.length > 0;

  return (
    <div className={cn('relative w-full', className)}>
      {/* Search Input */}
      <div
        className={cn(
          'relative flex items-center w-full bg-white border-2 rounded-lg transition-all duration-200',
          styles.container,
          isFocused
            ? 'border-sage ring-4 ring-sage/15'
            : 'border-warm-gray/20 hover:border-warm-gray/40'
        )}
      >
        <Search
          className={cn(
            'absolute left-4 text-warm-gray transition-colors',
            styles.icon,
            isFocused && 'text-sage'
          )}
        />
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={cn(
            'w-full h-full pl-12 pr-12 bg-transparent outline-none text-navy placeholder:text-warm-gray/60',
            styles.input
          )}
          aria-label="Search resources"
          aria-expanded={showDropdown}
          aria-controls="search-suggestions"
          aria-activedescendant={
            selectedIndex >= 0 ? `suggestion-${selectedIndex}` : undefined
          }
          role="combobox"
        />

        {/* Loading/Clear button */}
        {(query || isSearching) && (
          <div className="absolute right-4 flex items-center gap-2">
            {isSearching && (
              <Loader2 className={cn('animate-spin text-warm-gray', styles.icon)} />
            )}
            {query && !isSearching && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                className="text-warm-gray hover:text-navy transition-colors"
                aria-label="Clear search"
              >
                <X className={styles.icon} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          id="search-suggestions"
          className="absolute z-50 w-full mt-2 bg-white border border-warm-gray/20 rounded-lg shadow-lifted overflow-hidden"
          role="listbox"
        >
          {/* Recent searches header */}
          {query.length < 2 && (recentSearches?.length ?? 0) > 0 && (
            <div className="flex items-center justify-between px-4 py-2 bg-cream/50 border-b border-warm-gray/10">
              <span className="text-xs font-medium text-warm-gray uppercase tracking-wide">
                Recent Searches
              </span>
              <button
                type="button"
                onClick={clearSearches}
                className="text-xs text-warm-gray hover:text-terracotta transition-colors"
              >
                Clear
              </button>
            </div>
          )}

          {/* Suggestions list */}
          <ul className="py-2 max-h-80 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.id}
                id={`suggestion-${index}`}
                role="option"
                aria-selected={index === selectedIndex}
              >
                <button
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                    index === selectedIndex
                      ? 'bg-sage/10 text-sage-dark'
                      : 'hover:bg-cream'
                  )}
                >
                  {/* Icon */}
                  <span className="flex-shrink-0">
                    {suggestion.type === 'recent' && (
                      <Clock className="w-4 h-4 text-warm-gray" />
                    )}
                    {suggestion.type === 'trending' && (
                      <TrendingUp className="w-4 h-4 text-terracotta" />
                    )}
                    {suggestion.type === 'diagnosis' && (
                      <span className="w-4 h-4 rounded bg-sage/20 flex items-center justify-center text-xs font-medium text-sage">
                        D
                      </span>
                    )}
                    {suggestion.type === 'resource' && (
                      <Search className="w-4 h-4 text-warm-gray" />
                    )}
                  </span>

                  {/* Title */}
                  <span className="flex-1 truncate text-navy">
                    {suggestion.title}
                  </span>

                  {/* Meta */}
                  {suggestion.meta && (
                    <span className="flex-shrink-0 text-xs text-warm-gray">
                      {suggestion.meta}
                    </span>
                  )}

                  {/* Arrow for direct links */}
                  {(suggestion.type === 'diagnosis' || suggestion.type === 'resource') && (
                    <ArrowRight className="w-4 h-4 text-warm-gray flex-shrink-0" />
                  )}
                </button>
              </li>
            ))}
          </ul>

          {/* Search all footer */}
          {query.length >= 2 && (
            <div className="border-t border-warm-gray/10">
              <button
                type="button"
                onClick={() => handleSearch(query)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sage font-medium hover:bg-sage/5 transition-colors"
              >
                <Search className="w-4 h-4" />
                <span>Search all for "{query}"</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Compact Search Button (for header)
// -----------------------------------------------------------------------------

interface SearchButtonProps {
  onClick?: () => void;
  className?: string;
}

export function SearchButton({ onClick, className }: SearchButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2 bg-cream hover:bg-cream-dark border border-warm-gray/20 rounded-lg transition-colors',
        className
      )}
      aria-label="Open search"
    >
      <Search className="w-4 h-4 text-warm-gray" />
      <span className="text-sm text-warm-gray hidden sm:inline">Search...</span>
      <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs text-warm-gray bg-white border border-warm-gray/20 rounded">
        <span className="text-[10px]">⌘</span>K
      </kbd>
    </button>
  );
}

export default ResourceSearchBar;

